import './critical.js';
import { Component } from '@theme/component';
import { changeMetaThemeColor } from '@theme/utilities';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #animationDelay = 150;

  #headerHeightFrame = null;
  #scrollFrame = null;
  #resizeFrame = null;

  #lastHeaderHeight = '';
  #scrollBound = false;
  #stickyEnabled = false;
  #hasGloboMenu = null;

  #isDesktop = () => window.innerWidth >= 750;

  #checkGloboMenu = () => {
    if (this.#hasGloboMenu !== null) return this.#hasGloboMenu;

    this.#hasGloboMenu = !!document.querySelector('.gm-menu, .gm-navbar, .gm-wrap');
    return this.#hasGloboMenu;
  };

  #resizeObserver = new ResizeObserver((entries) => {
    const entry = entries && entries[0];
    if (!entry) return;

    if (this.#headerHeightFrame) {
      cancelAnimationFrame(this.#headerHeightFrame);
    }

    this.#headerHeightFrame = requestAnimationFrame(() => {
      this.#headerHeightFrame = null;

      let height = 0;

      if (entry.borderBoxSize) {
        const borderSize = Array.isArray(entry.borderBoxSize)
          ? entry.borderBoxSize[0]
          : entry.borderBoxSize;
        height = Math.round(borderSize?.blockSize || 0);
      }

      if (!height && entry.contentRect) {
        height = Math.round(entry.contentRect.height || 0);
      }

      const newHeight = `${height}px`;

      if (this.#lastHeaderHeight !== newHeight) {
        this.#lastHeaderHeight = newHeight;
      }

      if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
        this.#updateMenuVisibility(false);
      }
    });
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (!this.#stickyEnabled) return;
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    const isDesktop = window.innerWidth >= 990;
    const hasGloboMenu = this.#checkGloboMenu();

    if (isDesktop && hasGloboMenu) {
      this.#updateMenuVisibility(false);
      return;
    }

    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    const isDesktop = window.innerWidth >= 990;
    const hasGloboMenu = this.#checkGloboMenu();

    if (isDesktop && hasGloboMenu) {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
      return;
    }

    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (!this.#stickyEnabled) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  #onScroll = () => {
    if (!this.#stickyEnabled) return;
    if (this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#handleWindowScroll();
    });
  };

  #handleViewportChange = () => {
    const stickyMode = this.getAttribute('sticky');
    const shouldEnableSticky = !!stickyMode && this.#isDesktop();

    if (shouldEnableSticky === this.#stickyEnabled) return;

    this.#stickyEnabled = shouldEnableSticky;

    if (this.#scrollBound) {
      document.removeEventListener('scroll', this.#onScroll);
      this.#scrollBound = false;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#offscreen = false;
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;

    if (!this.#stickyEnabled) {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'inactive';
      this.removeAttribute('data-animating');
      return;
    }

    this.#observeStickyPosition(stickyMode === 'always');

    if ((stickyMode === 'scroll-up' || stickyMode === 'always') && !this.#scrollBound) {
      document.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#scrollBound = true;
    }
  };

  #onWindowResize = () => {
    if (this.#resizeFrame) return;

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = null;
      this.#handleViewportChange();
    });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#lastHeaderHeight = '';

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.addEventListener('resize', this.#onWindowResize, { passive: true });

    this.#handleViewportChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#headerHeightFrame) {
      cancelAnimationFrame(this.#headerHeightFrame);
      this.#headerHeightFrame = null;
    }

    if (this.#scrollFrame) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = null;
    }

    if (this.#resizeFrame) {
      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = null;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('resize', this.#onWindowResize);

    if (this.#scrollBound) {
      document.removeEventListener('scroll', this.#onScroll);
      this.#scrollBound = false;
    }

    this.#lastHeaderHeight = '';
    this.#hasGloboMenu = null;
    this.#stickyEnabled = false;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}